//
// Copyright © 2026 ZeroDesk
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import type { Person } from '@hcengineering/contact'
import type { Doc, Ref, Space, Timestamp } from '@hcengineering/core'
import type { Department } from './index'

/**
 * Private space that holds all sensitive personnel data. Members are the HR team and workspace owners.
 * @public
 */
export type HRSpace = Space

/**
 * @public
 */
export enum PersonnelStatus {
  Preboarding = 'preboarding',
  Active = 'active',
  Leave = 'leave',
  Notice = 'notice',
  Left = 'left'
}

/**
 * @public
 */
export const personnelStatuses: PersonnelStatus[] = [
  PersonnelStatus.Preboarding,
  PersonnelStatus.Active,
  PersonnelStatus.Leave,
  PersonnelStatus.Notice,
  PersonnelStatus.Left
]

/**
 * @public
 */
export enum EmploymentKind {
  Employee = 'employee',
  PartTime = 'parttime',
  Minijob = 'minijob',
  WorkingStudent = 'workingstudent',
  Trainee = 'trainee',
  Intern = 'intern',
  Freelancer = 'freelancer',
  Executive = 'executive'
}

/**
 * @public
 */
export const employmentKinds: EmploymentKind[] = [
  EmploymentKind.Employee,
  EmploymentKind.PartTime,
  EmploymentKind.Minijob,
  EmploymentKind.WorkingStudent,
  EmploymentKind.Trainee,
  EmploymentKind.Intern,
  EmploymentKind.Freelancer,
  EmploymentKind.Executive
]

/**
 * @public
 */
export enum Gender {
  Female = 'female',
  Male = 'male',
  Diverse = 'diverse',
  Unspecified = 'unspecified'
}

/**
 * @public
 */
export enum MaritalStatus {
  Single = 'single',
  Married = 'married',
  Partnership = 'partnership',
  Divorced = 'divorced',
  Widowed = 'widowed'
}

/**
 * @public
 */
export enum HealthInsuranceType {
  Statutory = 'statutory',
  Private = 'private'
}

/**
 * Denomination relevant for church tax.
 * @public
 */
export enum Denomination {
  None = 'none',
  Catholic = 'catholic',
  Protestant = 'protestant',
  Other = 'other'
}

/**
 * Personnel file (Personalakte). Exactly one per person, lives in the private HR space.
 * The file hangs on the person, not on the workspace account, so people without login are possible.
 * @public
 */
export interface PersonnelFile extends Doc {
  person: Ref<Person>

  personnelNumber: string
  status: PersonnelStatus
  employmentKind: EmploymentKind

  entryDate?: Timestamp | null
  exitDate?: Timestamp | null
  probationEnd?: Timestamp | null

  // Current snapshot of the employment, maintained from contracts later on
  position?: string
  department?: Ref<Department> | null
  manager?: Ref<Person> | null
  location?: string
  costCenter?: string

  // Personal
  birthDate?: Timestamp | null
  birthPlace?: string
  gender?: Gender
  nationality?: string
  maritalStatus?: MaritalStatus
  street?: string
  zip?: string
  city?: string
  country?: string
  privateEmail?: string
  privatePhone?: string
  emergencyContactName?: string
  emergencyContactPhone?: string
  emergencyContactRelation?: string

  // Tax
  taxId?: string
  taxNumber?: string
  taxClass?: number | null
  taxFactor?: string
  childAllowances?: string
  denomination?: Denomination

  // Social security
  socialSecurityNumber?: string
  healthInsurer?: string
  healthInsuranceType?: HealthInsuranceType
  contributionGroupKey?: string
  personGroupKey?: string
  activityKey?: string

  // Bank
  iban?: string
  bic?: string
  accountHolder?: string

  // Residence and special categories
  workPermitType?: string
  workPermitValidUntil?: Timestamp | null
  disabilityDegree?: number | null

  // Internal notes, visible to the HR team only
  notes?: string

  // Marks generated demo data
  demo?: boolean
}

/**
 * Sensitive fields of a personnel file. Reading them in clear text is logged.
 * @public
 */
export type SensitiveField =
  | 'taxId'
  | 'taxNumber'
  | 'socialSecurityNumber'
  | 'iban'
  | 'bic'
  | 'disabilityDegree'

/**
 * @public
 */
export type AccessAction = 'reveal' | 'export'

/**
 * Entry of the access log for sensitive fields. Who and when are stored in `modifiedBy` and `createdOn`.
 * @public
 */
export interface AccessLogEntry extends Doc {
  person: Ref<Person>
  field: string
  action: AccessAction
}
